import React, { useEffect } from 'react';
import { ProjectType, TaskType, useSmartTagsService } from 'nekst-api';
import {
  BaseInputProps,
  SmartTagsWysiwyg,
  Tag,
  useFormContextRequired,
} from 'ui-builder';

interface BaseSmartTagsWysiwygProps extends BaseInputProps {
  projectId?: number | ((data: Record<string, any>) => number | undefined);
  planId?: number;
  projectType?: ProjectType;
  taskType: TaskType;
  hideControls?: boolean;
}

function BaseSmartTagsWysiwyg(props: BaseSmartTagsWysiwygProps) {
  const [availableTags, setAvailableTags] = React.useState<Tag[]>([]);

  const formContext = useFormContextRequired();
  const smartTagsService = useSmartTagsService();

  const data = formContext.data || {};

  let projectIdValue: number | undefined;

  if (typeof props.projectId === 'function') {
    projectIdValue = props.projectId(data);
  } else {
    projectIdValue = props.projectId;
  }

  useEffect(() => {
    async function loadTags() {
      if (projectIdValue) {
        const tags = await smartTagsService.getSmartTagsByProjectId(
          projectIdValue,
          props.taskType
        );

        setAvailableTags(tags);
      } else if (props.planId) {
        const tags = await smartTagsService.getSmartTagsByPlanId(
          props.planId,
          props.taskType
        );

        setAvailableTags(tags);
      } else {
        setAvailableTags([]);
      }
    }

    loadTags();
  }, [projectIdValue, props.projectType]);

  return (
    <SmartTagsWysiwyg
      {...props}
      availableTags={availableTags}
      hideControls={props.hideControls}
    />
  );
}

interface SmartTagsWysiwygProps extends BaseInputProps {
  projectId?: number | ((data: Record<string, any>) => number | undefined);
  projectType?: ProjectType;
}

export function EmailProjectTaskSmartTagsWysiwyg(props: SmartTagsWysiwygProps) {
  return (
    <BaseSmartTagsWysiwyg
      {...props}
      taskType={TaskType.EMAIL}
      projectType={props.projectType}
      projectId={props.projectId}
    />
  );
}

export function EventProjectTaskSmartTagsWysiwyg(props: SmartTagsWysiwygProps) {
  return (
    <BaseSmartTagsWysiwyg
      {...props}
      taskType={TaskType.EVENT}
      projectType={props.projectType}
      projectId={props.projectId}
    />
  );
}

export function SmsProjectTaskSmartTagsWysiwyg(props: SmartTagsWysiwygProps) {
  return (
    <BaseSmartTagsWysiwyg
      {...props}
      taskType={TaskType.SMS}
      projectType={props.projectType}
      projectId={props.projectId}
      hideControls
    />
  );
}

export function EmailPlanTaskSmartTagsWysiwyg(
  props: SmartTagsWysiwygProps & { planId: number }
) {
  return (
    <BaseSmartTagsWysiwyg
      {...props}
      taskType={TaskType.EMAIL}
      planId={props.planId}
      projectType={props.projectType}
    />
  );
}

export function SmsPlanTaskSmartTagsWysiwyg(
  props: SmartTagsWysiwygProps & { planId: number }
) {
  return (
    <BaseSmartTagsWysiwyg
      {...props}
      taskType={TaskType.SMS}
      planId={props.planId}
      projectType={props.projectType}
      hideControls
    />
  );
}

export function EventPlanTaskSmartTagsWysiwyg(
  props: SmartTagsWysiwygProps & { planId: number }
) {
  return (
    <BaseSmartTagsWysiwyg
      {...props}
      taskType={TaskType.EVENT}
      planId={props.planId}
      projectType={props.projectType}
    />
  );
}

export function RoadmapSmartTagsWysiwyg(
  props: SmartTagsWysiwygProps & { planId?: number }
) {
  return (
    <BaseSmartTagsWysiwyg
      {...props}
      taskType={TaskType.STANDARD}
      planId={props.planId}
      projectId={props.projectId}
      projectType={props.projectType}
    />
  );
}
